import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { packagesService } from '../services/packagesService';
import { 
  CubeIcon, 
  CalendarIcon, 
  MapPinIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { state } = useApp();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };


  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await packagesService.getDashboardData();
        
        if (response.success) {
          setDashboardData(response.data);
        } else {
          setError('Failed to fetch dashboard data');
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to fetch dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Transform API data to match UI expectations
  const displayData = dashboardData ? {
    overview: {
      totalPackages: dashboardData.overall?.totalPackages || 0,
      totalHolidayPackages: dashboardData.overall?.totalPackages || 0, // Assuming all are holiday packages
      activeBookings: dashboardData.overall?.activeBookings || 0,
      totalRevenue: dashboardData.overall?.totalRevenue || 0,
      pendingBookings: dashboardData.recentBookings?.filter(booking => booking.status === 'PENDING').length || 0,
      completedBookings: dashboardData.recentBookings?.filter(booking => booking.status === 'CONFIRMED').length || 0
    },
    recentPackages: dashboardData.recentPackages?.map(pkg => ({
      id: pkg.id,
      title: pkg.title,
      type: 'Holiday', // All packages are holiday packages
      status: pkg.status,
      revenue: pkg.price || 0,
      description: pkg.description,
      duration: pkg.duration,
      createdAt: pkg.createdAt,
      vendor: pkg.vendor
    })) || [],
    recentBookings: dashboardData.recentBookings?.map(booking => ({
      id: booking.id,
      customerName: booking.customerName,
      packageTitle: booking.package?.title || 'Unknown Package',
      date: booking.createdAt,
      status: booking.status,
      amount: booking.totalAmount || 0,
      email: booking.email,
      phone: booking.phone,
      numberOfPeople: booking.numberOfPeople,
      destination: booking.package?.destination
    })) || [],
    topDestinations: dashboardData.topDestinations?.map(dest => ({
      name: dest.destination,
      bookings: dest.bookingCount || 0,
      revenue: dest.totalRevenue || 0,
      totalPersons: dest.totalPersons || 0
    })) || []
  } : {
    overview: {
      totalPackages: 0,
      totalHolidayPackages: 0,
      activeBookings: 0,
      totalRevenue: 0,
      pendingBookings: 0,
      completedBookings: 0
    },
    recentPackages: [],
    recentBookings: [],
    topDestinations: []
  };

  if (loading) {
    return (
      <div className="h-full bg-gray-50">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
        <main className="p-6">
          <div className="flex items-center justify-center min-h-96">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gray-50">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Error loading dashboard</p>
          </div>
        </div>
        <main className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back, {state.user?.name || 'Vendor'}</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        {/* Overview Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Packages */}
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CubeIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Packages</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {displayData.overview.totalPackages}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {displayData.overview.totalHolidayPackages} holiday packages
                </p>
              </div>
            </div>
          </div>

          {/* Active Bookings */}
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Bookings</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {displayData.overview.activeBookings}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {displayData.overview.pendingBookings} pending
                </p>
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${displayData.overview.totalRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  +12.5% from last month
                </p>
              </div>
            </div>
          </div>

          {/* Completion Rate */}
          <div className="card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Completion Rate</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {displayData.overview.activeBookings > 0 ? Math.round((displayData.overview.completedBookings / displayData.overview.activeBookings) * 100) : 0}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {displayData.overview.completedBookings} completed
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Packages - Full Width */}
        <div className="mb-8">
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Packages</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {displayData.recentPackages.map((pkg) => (
                    <div key={pkg.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          {pkg.type === 'Holiday' ? (
                            <MapPinIcon className="h-6 w-6 text-primary-600" />
                          ) : (
                            <CubeIcon className="h-6 w-6 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{pkg.title}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {truncateText(pkg.description, 100)}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              pkg.status === 'PUBLISHED' 
                                ? 'bg-green-100 text-green-800' 
                                : pkg.status === 'DRAFT'
                                ? 'bg-yellow-100 text-yellow-800'
                                : pkg.status === 'SUSPENDED'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {pkg.status}
                            </span>
                            <span className="text-xs text-gray-500">{pkg.duration}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">${pkg.revenue.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">Revenue</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        {/* Top Destinations - Full Width */}
        <div className="mb-8">
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Top Destinations</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {displayData.topDestinations.map((destination, index) => (
                  <div key={destination.name} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">{index + 1}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{destination.name}</p>
                        <p className="text-xs text-gray-500">{destination.bookings} bookings</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">${destination.revenue.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Bookings - Full Width */}
        <div className="mb-8">
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Bookings</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {displayData.recentBookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <UserGroupIcon className="h-6 w-6 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{booking.customerName}</p>
                        <p className="text-xs text-gray-500">{booking.packageTitle}</p>
                        <p className="text-xs text-gray-400">
                          {booking.numberOfPeople} people • {new Date(booking.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          booking.status === 'CONFIRMED' 
                            ? 'bg-green-100 text-green-800' 
                            : booking.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mt-1">${booking.amount.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
};

export default Dashboard;
